"use client";

import Image from "next/image";
import type { MouseEvent } from "react";
import chevron from "@/assets/decor/scroll-chevron.svg";
import { cn } from "@/shared/lib/cn";

// In Figma the chevrons sit 16.5px apart in a 44 × 52 group, i.e. at 0%, 31.73% and 63.46% of its height.
const CHEVRONS = [
  { position: "top-0", animation: "motion-safe:animate-chevron-top" },
  { position: "top-[31.73%]", animation: "motion-safe:animate-chevron-middle" },
  { position: "top-[63.46%]", animation: "motion-safe:animate-chevron-bottom" },
];

// Figma's prototype scrolls to the cards over 300ms with "ease out", i.e. cubic-bezier(0, 0, 0.58, 1).
const SCROLL_DURATION_MS = 300;
const EASE_OUT = { x1: 0, y1: 0, x2: 0.58, y2: 1 };

function cubicBezier(progress: number, { x1, y1, x2, y2 }: typeof EASE_OUT) {
  const sample = (a: number, b: number, t: number) => 3 * a * t * (1 - t) ** 2 + 3 * b * t ** 2 * (1 - t) + t ** 3;
  let t = progress;
  for (let i = 0; i < 8; i++) {
    const slope = 3 * x1 * (1 - t) ** 2 + 6 * (x2 - x1) * t * (1 - t) + 3 * (1 - x2) * t ** 2;
    if (Math.abs(slope) < 1e-6) break;
    t -= (sample(x1, x2, t) - progress) / slope;
  }
  return sample(y1, y2, Math.min(Math.max(t, 0), 1));
}

type ScrollCueProps = {
  targetId: string;
  label: string;
  className?: string;
};

export function ScrollCue({ targetId, label, className }: ScrollCueProps) {
  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    const target = document.getElementById(targetId);
    if (!target) return;
    event.preventDefault();

    const start = window.scrollY;
    const distance = target.getBoundingClientRect().top;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      window.scrollTo(0, start + distance);
      return;
    }

    const startTime = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - startTime) / SCROLL_DURATION_MS, 1);
      window.scrollTo(0, start + distance * cubicBezier(progress, EASE_OUT));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  return (
    <a href={`#${targetId}`} aria-label={label} onClick={handleClick} className={cn("relative aspect-44/52", className)}>
      {CHEVRONS.map(({ position, animation }) => (
        <Image key={position} src={chevron} alt="" className={cn("absolute left-0 h-auto w-full", position, animation)} />
      ))}
    </a>
  );
}
